import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Rating,
    TextField,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import DetailsGrid from "./DetailsGrid";
import OrderChat from "./OrderChat";
import NotFound from "../../components/NotFoundService/NotFoundService";
import { rateOrder, addFeedback } from "../../redux/slices/myAccount/rateOrder";
import RatingComponent from "./RatingComponent";
import SuccessMessage from "./SuccessMessage";
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { useDispatch, useSelector } from "react-redux";
import { fetchOrderDetails } from "../../redux/slices/myAccount/orderDetailsSlice";
import Loading from "../../components/UI/Loading/Loading";
import { useParams } from "react-router-dom";
const OrderDetails = () => {
    const { translations, direction, language } = useLanguage();
    const t = translations;
    const { orderId: paramOrderId } = useParams(); // Extract from URL
    const location = useLocation();
    const { OrderId: stateOrderId } = location.state || {}; // Extract from state if passed
    // Use paramOrderId (from URL) or fallback to stateOrderId (from navigation state)
    const OrderId = paramOrderId || stateOrderId;  
    const dispatch = useDispatch();
    const { order, loading, error } = useSelector((state) => state.orderDetails);
    const [rating, setRating] = useState(order?.Rate || 0);
    const [feedback, setFeedback] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [showRatingComponent, setShowRatingComponent] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false); // State for success message
    const { isLoggedIn, user, token } = useSelector((state) => state.login);
    const [successTitle, setSuccessTitle] = useState(""); // State for title
    const [successMessage, setSuccessMessage] = useState("");
    const [feedbackError, setFeedbackError] = useState("");
    useEffect(() => {
        if (OrderId) {
           
            dispatch(fetchOrderDetails({ OrderId, token, language }));  // Fetch the order details based on the orderId
        }
    }, [OrderId, dispatch, language]);
    useEffect(() => {
        if (order) {
            setRating(order.Rate || 0);
        }
    }, [order]);

    const handleRatingSubmit = (orderId, rate) => {
        dispatch(rateOrder({ orderId: order.OrderId, rate, token }));
        setRating(rate);
        setShowRatingComponent(false);
        setSuccessTitle(t.successTitle);
        setSuccessMessage(t.successMessage);
        setShowSuccess(true); // Show success message
    };
    const handleCloseSnackbar = () => {
        setShowSuccess(false); // Hide success message
    };

    const handleOpenRatingComponent = () => {
        setShowRatingComponent(true);
    };

    const handleCancelRating = () => {
        setShowRatingComponent(false);
    };
    const handleFeedbackChange = (e) => {
        const value = e.target.value;
        setFeedback(value);

        // Real-time validation
        if (value.trim() === "") {
            setFeedbackError(t.feedbackErrorempty);
        } else if (value.length < 5) {
            setFeedbackError(t.feedbackErrorshort);
        } else {
            setFeedbackError(""); // Clear error if valid
        }
    };
    const handleSubmitFeedback = () => {
        // Validate the feedback
        if (feedback.trim() === "") {
            setFeedbackError(t.feedbackErrorempty);
            return; // Stop the submission if feedback is empty
        }
        if (feedback.length < 5) {
            setFeedbackError(t.feedbackErrorshort);
            return; // Stop the submission if feedback is too short
        }

        // Clear the error and dispatch feedback
        setFeedbackError("");
        dispatch(addFeedback({ orderId: order.OrderId, feedback, token }));
        setSuccessTitle(t.feedbacksuccessTitle);
        setSuccessMessage(t.feedbacksuccessMessage);
        setShowSuccess(true);
        setFeedback(order.FeedBack);
    };
    if (loading) {
        return <Loading />;
    }

    if (!order) {
        return <NotFound message={t.noOrderData} />;
    }
    const isOrderClosed = order.Status === 3;
    // console.log(isOrderClosed);

    return (
        <Box sx={{ padding: "16px", minHeight: "100vh" }}>
            <Paper elevation={3} sx={{ padding: "28px", borderRadius: 2 }}>
                <Box
                    sx={{
                        background: "linear-gradient(0deg, #0E518A, #0E518A)",
                        padding: 3,
                        borderRadius: "20px",
                        color: "white",
                    }}
                >
                    <Typography sx={{ fontSize: "20px", fontWeight: 700, pb: "16px" }}>
                        {t.Order} #{order.OrderNumber}
                    </Typography>
                    <Typography sx={{ fontSize: "16px", pb: "8px", }}>
                        {t.status}: <span style={{
                            color:
                                order.StatusName === 'New'
                                    ? "#209F84"
                                    : order.status === "Closed"
                                        ? "#FF2C53"
                                        : "#FF5C00",
                        }}>{order.StatusName}</span>
                    </Typography>
                    <Typography sx={{ fontSize: "16px", pb: "8px" }}>
                        {t.date}: <span style={{ color: "#C3C3C3" }}>{order.RequestDate}</span>
                    </Typography>
                </Box>

                <DetailsGrid order={order} t={t} />
                {isOrderClosed && (
                    <>
                        <Box mt={4} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box
                                sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}
                                onClick={rating === 0 ? handleOpenRatingComponent : null}
                            >
                                <Typography variant="body1" sx={{ fontSize: "18px", fontWeight: 600 }}>
                                    {t.rateThisOrder}:
                                </Typography>
                                <Rating
                                    name="order-rating"
                                    value={rating}
                                    readOnly={rating !== 0}
                                    precision={0.5}
                                    sx={{
                                        color: "var(--primary-color)",
                                        cursor: "pointer",
                                        pointerEvents: "auto",
                                        direction: "ltr"
                                    }}

                                />
                            </Box>
                        </Box>

                        <Box mt={2}>
                            <TextField
                                label={t.feedbackplaceholder}
                                fullWidth
                                multiline
                                  variant="filled"
                                rows={4}
                                value={order.FeedBack || feedback}
                                onChange={handleFeedbackChange}
                                sx={{
                                    mb: 2,
                                    mt:2,
                                    "& .MuiInputLabel-root": {
                                        mt:2,
                                        left: direction === "rtl" ? "auto" : "10px !important",
                                        right: direction === "rtl" ? "20px !important" : "auto",
                                        textAlign: direction === "rtl" ? "right !important" : "left !important", // Align label text based on direction
                                    },
                                    // Ensure the label stays aligned correctly when focused
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        mt:2,
                                        left: direction === "rtl" ? "auto" : "10px !important", // Align properly on focus in RTL
                                        right: direction === "rtl" ? "20px !important" : "auto", // Align properly on focus in RTL
                                        textAlign: direction === "rtl" ? "right !important" : "left !important", // Label text on focus
                                    },
                                    // Align input text properly based on the direction
                                    "& .MuiInputBase-root": {
                                        mt:2,
                                        direction: direction ,
                                        textAlign: direction === "rtl" ? "right !important" : "left !important", // Input text alignment based on direction
                                    },
                                    // For multiline text area inside the TextField
                                    "& .MuiInputBase-root textarea": {
                                        mt:2,
                                        textAlign: direction === "rtl" ? "right !important" : "left !important", // Multiline text alignment
                                    },
                                }}

                                error={!!feedbackError && !order.FeedBack} // Show error if no feedback and error exists
                                helperText={(!order.FeedBack && feedbackError) || ''}
                                disabled={!!order.feedback}
                              
                            />
                            {!order.FeedBack && (
                                <Button
                                    onClick={handleSubmitFeedback}
                                    sx={{
                                        backgroundColor: "var(--primary-color)",
                                        color: "var(--secondary-color)",
                                        "&:hover": {
                                            backgroundColor: "var(--primary-color-dark)",
                                        },
                                    }}
                                >
                                    {t.feedbacksubmitButton}
                                </Button>
                            )}
                        </Box>
                    </>
                )}
                <OrderChat order={order} direction={direction} t={t} />
            </Paper>

            {showRatingComponent && (
                <RatingComponent
                    orderId={order.OrderId}
                    currentRating={rating}
                    onSubmitRating={handleRatingSubmit}
                    open={showRatingComponent}
                    onClose={handleCancelRating}
                    direction={direction}
                    t={t}
                />
            )}


            <SuccessMessage
                open={showSuccess}
                onClose={handleCloseSnackbar}
                title={successTitle} // Dynamic title
                message={successMessage} // Dynamic message
            />
        </Box>
    );
};

export default OrderDetails;
